import type { CertificationStatus, SignalDefinition, SignalDefinitionStatus, SignalMethod, SignalOrigin } from "@ti/core";
import { Binary, Brain, Compass, Package, PenLine, ScrollText, Sparkles, Waypoints } from "lucide-react";
import type { ReactNode } from "react";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/* Shared vocabulary for the signal studio, definition detail and review queue (doc 15). */

export const METHODS: SignalMethod[] = ["rules", "embedding", "decision", "llm_rubric"];

export const METHOD_META: Record<SignalMethod, { label: string; icon: ReactNode; how: string; when: string; cost: string }> = {
  rules: {
    label: "Rules",
    icon: <Binary />,
    how: "Deterministic predicates over spans, tools, events and text patterns.",
    when: "The pattern is structural: tool X before Y, phrase lists, numeric thresholds.",
    cost: "Effectively free",
  },
  embedding: {
    label: "Embedding",
    icon: <Waypoints />,
    how: "Similarity of turns to labeled positives vs negatives, with a calibrated threshold.",
    when: "Enough labeled examples (30+ positives) and a stable meaning.",
    cost: "Very low",
  },
  decision: {
    label: "Decision",
    icon: <Compass />,
    how: "A decision model answers the definition as a yes/no question over the transcript and returns a calibrated probability; it fires above the threshold.",
    when: "Most semantic signals. Runs on every session as one more question in the existing decision pass.",
    cost: "Low: input tokens only",
  },
  llm_rubric: {
    label: "LLM rubric",
    icon: <Brain />,
    how: "A generative model applies the rubric and writes an explanation for each hit.",
    when: "Nuanced signals that need reasoning, or where reviewers need the explanation. Sampled + flagged sessions only.",
    cost: "Highest: generated tokens",
  },
};

export function MethodBadge({ method, className }: { method: SignalMethod; className?: string }) {
  const m = METHOD_META[method];
  return (
    <Tooltip content={<span className="block max-w-64">{m.how}</span>}>
      <span tabIndex={0} className={cn("inline-flex items-center gap-1 whitespace-nowrap text-xs text-fg-2 [&_svg]:size-3.5 [&_svg]:text-fg-3", className)}>
        {m.icon}
        {m.label}
      </span>
    </Tooltip>
  );
}

export const ORIGIN_META: Record<SignalOrigin, { label: string; icon: ReactNode; help: string }> = {
  catalog: { label: "Catalog", icon: <ScrollText />, help: "Part of the traces.insure signal catalog" },
  pack: { label: "Pack", icon: <Package />, help: "Defined by an enabled pack" },
  discovered: { label: "Discovered", icon: <Sparkles />, help: "Found by the nightly discovery job in your sessions" },
  custom: { label: "Custom", icon: <PenLine />, help: "Written by your team in plain language" },
};

export function OriginLabel({ origin }: { origin: SignalOrigin }) {
  const o = ORIGIN_META[origin];
  return (
    <Tooltip content={o.help}>
      <span tabIndex={0} className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-fg-2 [&_svg]:size-3.5 [&_svg]:text-fg-3">
        {o.icon}
        {o.label}
      </span>
    </Tooltip>
  );
}

export const STATUS_ORDER: SignalDefinitionStatus[] = ["proposed", "backtesting", "shadow", "active", "paused", "planned", "retired", "dismissed"];

export const STATUS_HELP: Record<SignalDefinitionStatus, string> = {
  planned: "Specified in the catalog but not detected yet: creates no signals or issues and isn't counted as monitoring.",
  proposed: "Discovered by the engine or drafted from a pain point; not running.",
  backtesting: "Running over a sample of the last 30 days of stored sessions.",
  shadow: "Evaluated on new sessions; hits are reviewable, but no alerts and no issues.",
  active: "Live: alerts, dashboards and optional issue creation.",
  paused: "Stopped; keeps its history.",
  retired: "Permanently off.",
  dismissed: "Proposal rejected; the engine won't propose this pattern again.",
};

export type LifecycleAction = "accept" | "dismiss" | "shadow" | "activate" | "pause" | "retire" | "nominate" | "edit";

/**
 * Transitions the API accepts from each status for customer (analytics-tier) definitions. The server
 * is the authority (it also enforces the promotion bar); this only decides which buttons to show.
 */
export const ALLOWED_ACTIONS: Record<SignalDefinitionStatus, LifecycleAction[]> = {
  planned: [],
  proposed: ["accept", "dismiss"],
  backtesting: ["shadow", "edit", "dismiss", "retire"],
  shadow: ["activate", "pause", "edit", "retire"],
  active: ["pause", "shadow", "nominate", "edit", "retire"],
  paused: ["activate", "shadow", "edit", "retire"],
  retired: [],
  dismissed: [],
};

export function allowedActions(d: Pick<SignalDefinition, "status" | "tierClass" | "certification">): LifecycleAction[] {
  if (d.tierClass === "risk") return [];
  return ALLOWED_ACTIONS[d.status].filter((a) => a !== "nominate" || d.certification === "none" || d.certification === "rejected");
}

/** Promotion bar from shadow to active (doc 15 §2). */
export const PROMOTION = { minLabels: 20, minPrecision: 0.8 } as const;

/**
 * Probability above which a decision signal fires until its threshold is tuned from reviews. The
 * contract doesn't carry per-version thresholds yet, so the review queue shows this default.
 */
export const DEFAULT_THRESHOLD = 0.7;

export const CERT_STEPS: CertificationStatus[] = ["none", "nominated", "under_review", "certified"];

export const CERT_LABEL: Record<CertificationStatus, string> = {
  none: "Not nominated",
  nominated: "Nominated",
  under_review: "Under review",
  certified: "Certified",
  rejected: "Rejected",
};

/** "All agents" or the agent names, resolved from ids. */
export function scopeLabel(agentIds: string[] | null, names: Map<string, string>) {
  if (!agentIds) return "All agents";
  return agentIds.map((id) => names.get(id) ?? id).join(", ");
}

/** Percentage change between the first and second half of a trend. */
export function trendDelta(trend: number[]): number {
  const half = Math.floor(trend.length / 2);
  const prev = trend.slice(0, half).reduce((a, b) => a + b, 0);
  const cur = trend.slice(half).reduce((a, b) => a + b, 0);
  return prev ? Math.round(((cur - prev) / prev) * 100) : 0;
}
